/**
 * 表示中の交換QRの端末保存
 * 交換QRは発行し直すと前のQRが使用済みになるため、画面を離れた・再読み込みした後も同じQRを出し続けられるよう端末に保存します。
 * QRが有効かどうかは端末では判定せず、画面が期限延長（exchange_keepalive）でサーバーに確かめます。
 * 単価・小計・合計は保存しません（必要ptは変わるため、画面が描画のたびに最新の景品カタログから導きます）。
 */

import webStorage from '../../../shared/services/webStorage';
import { logWarn } from '../../../shared/utils/logger';
import { toStoredExchangeQrItem } from '../utils/exchangeQrPricing';

/** 保存キー */
const DISPLAYED_EXCHANGE_QR_STORAGE_KEY = '@ikomasai/displayed_exchange_qr';

/** 保存を返す上限。交換QRはスタッフの読み取りで延びても発行から120分で終わるため、それより古い保存は使わない */
export const DISPLAYED_EXCHANGE_QR_MAX_AGE_MS = 120 * 60 * 1000;

/** 保存する交換QR。画面ごとに足りない項目（景品の一覧など）は読み戻した画面が補う */
export type DisplayedExchangeQr = {
  /** 交換用コード */
  exchangeToken: string;
  /** 交換QRのID */
  exchangeSessionId: string;
  /** 最後に分かった期限 */
  expiresAt: string;
  /** その他の表示用の値（交換予定の景品・注文など） */
  [key: string]: unknown;
};

/** 保存値 */
type StoredDisplayedExchangeQr = {
  /** 発行した来場者セッション。別のセッションには返さない */
  sessionToken: string;
  /** このQRを最初に保存した時刻（epoch ms） */
  issuedAt: number;
  /** 交換QR */
  qr: DisplayedExchangeQr;
};

/**
 * 保存値の形を確かめる
 * @param {unknown} value - JSONから読んだ値
 * @returns {value is StoredDisplayedExchangeQr} 使える形ならtrue
 */
const isStoredDisplayedExchangeQr = (value: unknown): value is StoredDisplayedExchangeQr => {
  const stored = value as StoredDisplayedExchangeQr | null;
  return Boolean(stored)
    && typeof stored.sessionToken === 'string'
    && typeof stored.issuedAt === 'number'
    && Boolean(stored.qr)
    && typeof stored.qr.exchangeToken === 'string'
    && stored.qr.exchangeToken !== ''
    && typeof stored.qr.exchangeSessionId === 'string'
    && stored.qr.exchangeSessionId !== ''
    && typeof stored.qr.expiresAt === 'string';
};

/**
 * 交換QRから価格の値を除く。旧形式の保存（単価・小計・合計あり）を読むときも通す
 * @param {DisplayedExchangeQr} qr - 交換QR
 * @returns {DisplayedExchangeQr} 価格の値を除いた交換QR
 */
const omitDerivedPrices = (qr: DisplayedExchangeQr): DisplayedExchangeQr => {
  const { totalPoints: _totalPoints, totalQuantity: _totalQuantity, ...rest } = qr;
  return Array.isArray(rest.items)
    ? { ...rest, items: rest.items.filter((item) => item && typeof item === 'object').map(toStoredExchangeQrItem) }
    : rest;
};

/**
 * 保存値を読む。壊れた値は消してnullを返す
 * @returns {Promise<StoredDisplayedExchangeQr | null>} 保存値
 */
const readStored = async (): Promise<StoredDisplayedExchangeQr | null> => {
  const serialized = await webStorage.getItem(DISPLAYED_EXCHANGE_QR_STORAGE_KEY);
  if (!serialized) {
    return null;
  }

  try {
    /** JSONから読んだ値 */
    const parsed: unknown = JSON.parse(serialized);
    if (isStoredDisplayedExchangeQr(parsed)) {
      return parsed;
    }
  } catch (error) {
    logWarn('保存していた交換QRが壊れていたため削除します。', {
      message: error instanceof Error ? error.message : String(error),
    });
  }
  await webStorage.removeItem(DISPLAYED_EXCHANGE_QR_STORAGE_KEY);
  return null;
};

/**
 * 保存した交換QRを読み戻す
 * 別のセッションの保存と、延長の上限を過ぎた保存は返さない（上限を過ぎた保存は消す）。
 * @param {string} sessionToken - 来場者セッショントークン
 * @returns {Promise<DisplayedExchangeQr | null>} 交換QR。無ければnull
 */
export const loadDisplayedExchangeQr = async (sessionToken: string): Promise<DisplayedExchangeQr | null> => {
  try {
    const stored = await readStored();
    if (!stored || stored.sessionToken !== sessionToken) {
      return null;
    }
    if (Date.now() - stored.issuedAt > DISPLAYED_EXCHANGE_QR_MAX_AGE_MS) {
      await webStorage.removeItem(DISPLAYED_EXCHANGE_QR_STORAGE_KEY);
      return null;
    }
    return omitDerivedPrices(stored.qr);
  } catch (error) {
    // 読めない端末では従来どおり保存なしで動く。
    logWarn('保存していた交換QRを読めませんでした。', {
      message: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
};

/**
 * 表示中の交換QRを保存する
 * 同じQRを保存し直す場合は、最初に保存した時刻と、今回渡さなかった項目（別の画面が保存した注文など）を引き継ぐ。
 * @param {string} sessionToken - 来場者セッショントークン
 * @param {DisplayedExchangeQr} qr - 交換QR
 * @returns {Promise<void>}
 */
export const saveDisplayedExchangeQr = async (sessionToken: string, qr: DisplayedExchangeQr): Promise<void> => {
  try {
    const stored = await readStored();
    /** 同じ来場者の同じQRを保存し直すか */
    const isSameQr = Boolean(stored)
      && stored.sessionToken === sessionToken
      && stored.qr.exchangeSessionId === qr.exchangeSessionId;
    await webStorage.setItem(
      DISPLAYED_EXCHANGE_QR_STORAGE_KEY,
      JSON.stringify({
        sessionToken,
        issuedAt: isSameQr ? stored.issuedAt : Date.now(),
        qr: omitDerivedPrices(isSameQr ? { ...stored.qr, ...qr } : qr),
      } satisfies StoredDisplayedExchangeQr),
    );
  } catch (error) {
    // 保存できない端末では、画面を開いている間だけQRを出す（従来どおり）。
    logWarn('交換QRを端末に保存できませんでした。', {
      message: error instanceof Error ? error.message : String(error),
    });
  }
};

/**
 * 保存した交換QRを消す（QRが終わった・ログアウトした）
 * @returns {Promise<void>}
 */
export const clearDisplayedExchangeQr = async (): Promise<void> => {
  try {
    await webStorage.removeItem(DISPLAYED_EXCHANGE_QR_STORAGE_KEY);
  } catch (error) {
    logWarn('保存していた交換QRを消せませんでした。', {
      message: error instanceof Error ? error.message : String(error),
    });
  }
};
